import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { getBlogPost } from '@/services/blogService';
import { Calendar, Clock, User, Tag, Bookmark, Heart, MessageCircle, ArrowLeft } from 'lucide-react';
import SocialShare from '@/components/SocialShare';
import { getWebsiteSettings } from '@/lib/firebase';

const BlogPost = () => {
  const { id } = useParams();
  const [post, setPost] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<any>(null);
  const { toast } = useToast();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [postData, settingsData] = await Promise.all([
          getBlogPost(id),
          getWebsiteSettings()
        ]);
        console.log('BlogPost id:', id, 'postData:', postData);
        setPost(postData);
        setSettings(settingsData);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast({
          variant: 'destructive',
          title: 'خطأ في تحميل المقال',
          description: 'حدث خطأ أثناء تحميل المقال، يرجى المحاولة مرة أخرى',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, toast]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">المقال غير موجود أو الرابط غير صحيح</h2>
          <Link to="/blog">
            <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white">
              العودة إلى المدونة
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Handle createdAt as Timestamp, Date, or string
  let createdAtDate;
  if (post.createdAt && typeof post.createdAt.toDate === 'function') {
    createdAtDate = post.createdAt.toDate();
  } else if (typeof post.createdAt === 'string' || typeof post.createdAt === 'number') {
    createdAtDate = new Date(post.createdAt);
  } else {
    createdAtDate = new Date();
  }

  function formatDateTime(date) {
    if (!(date instanceof Date) || isNaN(date)) return '';
    return `${date.toLocaleDateString('ar-EG', { day: '2-digit', month: '2-digit', year: 'numeric' })} - ${date.toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}`;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 relative overflow-hidden">
      {/* Luxury Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl animate-float"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-br from-green-400/20 to-blue-400/20 rounded-full blur-3xl animate-float" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/3 left-1/3 w-72 h-72 bg-gradient-to-br from-yellow-400/15 to-pink-400/15 rounded-full blur-3xl animate-pulse-soft" style={{animationDelay: '1s'}}></div>
      </div>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center text-white">
            <Link to="/blog" className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-6 transition-colors">
              <ArrowLeft className="w-5 h-5" />
              العودة إلى المدونة
            </Link>
            <h1 className="text-5xl font-bold mb-6">{post.title}</h1>
            <div className="flex items-center justify-center gap-6 text-white/90">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{formatDateTime(createdAtDate)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="relative aspect-[21/9] rounded-3xl overflow-hidden mb-12 shadow-2xl">
            <img
              src={post.image || '/default-blog.png'}
              alt={post.title || 'صورة المقال'}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium">
                {post.category || 'بدون تصنيف'}
              </span>
              {Array.isArray(post.tags) && post.tags.map((tag: string) => (
                <span key={tag} className="bg-gray-100 text-gray-800 px-4 py-2 rounded-full text-sm font-medium">
                  {tag}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-4">
              <SocialShare 
                url={window.location.href}
                title={post.title || ''}
              />
              <Button variant="ghost" size="icon" className="rounded-full">
                <Bookmark className="w-5 h-5" />
              </Button>
              <Button variant="ghost" size="icon" className="rounded-full">
                <Heart className="w-5 h-5" />
              </Button>
            </div>
          </div>

          <div className="prose prose-lg max-w-none">
            <div dangerouslySetInnerHTML={{ __html: post.content || '' }} />
          </div>

          {/* Author Section */}
          <Card className="mt-12 bg-white/90 backdrop-blur-xl border-0 shadow-xl rounded-3xl">
            <CardContent className="p-8">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 rounded-full overflow-hidden">
                  <img
                    src={(settings?.doctor?.image) || '/default-doctor.png'}
                    alt={post.author || 'الكاتب'}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{post.author || ''}</h3>
                  <p className="text-gray-600">{settings?.doctor?.about || ''}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Comments Section */}
          <div className="mt-12">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">التعليقات</h3>
            <div className="space-y-6">
              {Array.isArray(post.comments) && post.comments.map((comment: any) => (
                <Card key={comment.id} className="bg-white/90 backdrop-blur-xl border-0 shadow-xl rounded-3xl">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full overflow-hidden">
                        <img
                          src={comment.authorImage || 'https://ui-avatars.com/api/?name=' + (comment.author || 'مستخدم')}
                          alt={comment.author || 'مستخدم'}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-bold text-gray-800">{comment.author || ''}</h4>
                          <span className="text-sm text-gray-500">
                            {comment.createdAt && typeof comment.createdAt.toDate === 'function'
                              ? comment.createdAt.toDate().toLocaleDateString('ar-SA')
                              : comment.createdAt
                                ? new Date(comment.createdAt).toLocaleDateString('ar-SA')
                                : ''}
                          </span>
                        </div>
                        <p className="text-gray-600">{comment.content || ''}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost; 